import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectByIdQuery,
  useDeleteProject,
  useProjectFilter,
  useRotateProjectApiKey,
  useTestProjectPipelineWebhook,
  useUpdateProject,
  useWipeProjectData,
} from '@/features/projects';
import { useMetricThresholdDrafts } from '@/features/projects/hooks/useMetricThresholdDrafts';
import { useNotifications } from '@/providers/notificationHooks';
import { useCurrentOrgRole, useMutationErrorSnackbar } from '@/shared/hooks';
import {
  metricThresholdsExecutionRuns,
  metricThresholdsPipelines,
  metricThresholdsSuites,
  metricThresholdsTests,
} from '@/shared/utils/metricsThresholds';
import type { AuthSession } from '@/auth/types';
import type { ProjectMetricThresholds } from '@flowqa/shared';

const defaultThresholds: ProjectMetricThresholds = {
  suites: metricThresholdsSuites,
  executionRuns: metricThresholdsExecutionRuns,
  tests: metricThresholdsTests,
  pipelines: metricThresholdsPipelines,
};

export type ProjectSettingsViewModel = {
  projectQuery: ReturnType<typeof useProjectByIdQuery>;
  project: ReturnType<typeof useProjectByIdQuery>['data'] | null;
  updateProject: ReturnType<typeof useUpdateProject>;
  rotateProjectApiKey: ReturnType<typeof useRotateProjectApiKey>;
  deleteProject: ReturnType<typeof useDeleteProject>;
  testProjectPipelineWebhook: ReturnType<typeof useTestProjectPipelineWebhook>;
  wipeProjectData: ReturnType<typeof useWipeProjectData>;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  canManageProject: boolean;
  canManageProjectLoading: boolean;
  nameValue: string;
  setNameValue: (value: string) => void;
  nameDirty: boolean;
  setNameDirty: (value: boolean) => void;
  apiKeyValue: string;
  setApiKeyValue: (value: string) => void;
  revokeOpen: boolean;
  setRevokeOpen: (open: boolean) => void;
  mutationError: ReturnType<typeof useMutationErrorSnackbar>;
  apiKeyError: ReturnType<typeof useMutationErrorSnackbar>;
  suiteThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setSuiteThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  suitePreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetSuiteThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  suiteThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  suiteThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  testThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setTestThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  testPreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetTestThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  testThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  testThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  executionRunThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setExecutionRunThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  executionRunPreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetExecutionRunThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  executionRunThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  executionRunThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  pipelineThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['drafts'];
  setPipelineThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  pipelinePreferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  resetPipelineThresholdDrafts: ReturnType<typeof useMetricThresholdDrafts>['resetDrafts'];
  pipelineThresholdsDirty: ReturnType<typeof useMetricThresholdDrafts>['isDirty'];
  pipelineThresholdsValid: ReturnType<typeof useMetricThresholdDrafts>['isValid'];
  notify: ReturnType<typeof useNotifications>['notify'];
  navigate: ReturnType<typeof useNavigate>;
};

export const useProjectSettingsViewModel = (
  session: AuthSession | null,
): ProjectSettingsViewModel => {
  const navigate = useNavigate();
  const mutationError = useMutationErrorSnackbar();
  const apiKeyError = useMutationErrorSnackbar();
  const { notify } = useNotifications();
  const updateProject = useUpdateProject();
  const rotateProjectApiKey = useRotateProjectApiKey();
  const deleteProject = useDeleteProject();
  const testProjectPipelineWebhook = useTestProjectPipelineWebhook();
  const wipeProjectData = useWipeProjectData();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [nameValue, setNameValue] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [revokeOpen, setRevokeOpen] = useState(false);
  const projectQuery = useProjectByIdQuery(selectedProjectId);
  const project = projectQuery.data ?? null;
  const { isAdmin: isOrgAdmin, isLoading: isOrgRoleLoading } = useCurrentOrgRole(session?.userId);
  const isPersonalProject = project !== null && project.organisationId === null;
  const isProjectOwner = isPersonalProject && project.createdById === session?.userId;
  const canManageProject = Boolean(session && (isOrgAdmin || isProjectOwner));
  const canManageProjectLoading = Boolean(session) && isOrgRoleLoading && !isProjectOwner;
  const suiteThresholdSource = project?.metricThresholds.suites ?? defaultThresholds.suites;
  const testThresholdSource = project?.metricThresholds.tests ?? defaultThresholds.tests;
  const executionRunThresholdSource =
    project?.metricThresholds.executionRuns ?? defaultThresholds.executionRuns;
  const pipelineThresholdSource =
    project?.metricThresholds.pipelines ?? defaultThresholds.pipelines;
  const {
    drafts: suiteThresholdDrafts,
    setDrafts: setSuiteThresholdDrafts,
    preferredUnitsRef: suitePreferredUnitsRef,
    resetDrafts: resetSuiteThresholdDrafts,
    isDirty: suiteThresholdsDirty,
    isValid: suiteThresholdsValid,
  } = useMetricThresholdDrafts(suiteThresholdSource);
  const {
    drafts: testThresholdDrafts,
    setDrafts: setTestThresholdDrafts,
    preferredUnitsRef: testPreferredUnitsRef,
    resetDrafts: resetTestThresholdDrafts,
    isDirty: testThresholdsDirty,
    isValid: testThresholdsValid,
  } = useMetricThresholdDrafts(testThresholdSource);
  const {
    drafts: executionRunThresholdDrafts,
    setDrafts: setExecutionRunThresholdDrafts,
    preferredUnitsRef: executionRunPreferredUnitsRef,
    resetDrafts: resetExecutionRunThresholdDrafts,
    isDirty: executionRunThresholdsDirty,
    isValid: executionRunThresholdsValid,
  } = useMetricThresholdDrafts(executionRunThresholdSource);
  const {
    drafts: pipelineThresholdDrafts,
    setDrafts: setPipelineThresholdDrafts,
    preferredUnitsRef: pipelinePreferredUnitsRef,
    resetDrafts: resetPipelineThresholdDrafts,
    isDirty: pipelineThresholdsDirty,
    isValid: pipelineThresholdsValid,
  } = useMetricThresholdDrafts(pipelineThresholdSource);

  useEffect(() => {
    if (projectQuery.isFetched && project === null) {
      void navigate('/projects', {
        replace: true,
        state: { notice: 'Project not found in this workspace' },
      });
    }
  }, [navigate, project, projectQuery.isFetched]);

  useEffect(() => {
    if (selectedProjectId !== null) return;
    void navigate('/execution-runs', { replace: true });
  }, [navigate, selectedProjectId]);

  useEffect(() => {
    if (!project) return;
    const nextOrgId = project.organisationId;
    if (selectedOrganisationId !== nextOrgId) {
      setSelectedOrganisationId(nextOrgId);
    }
  }, [project, selectedOrganisationId, setSelectedOrganisationId]);

  useEffect(() => {
    if (!project) return;
    setNameValue(project.name);
    setNameDirty(false);
  }, [project?.name]);

  useEffect(() => {
    if (!project) return;
    setApiKeyValue('');
  }, [project?.id, setApiKeyValue]);

  useEffect(() => {
    if (!project) return;
    resetSuiteThresholdDrafts(project.metricThresholds.suites);
    resetTestThresholdDrafts(project.metricThresholds.tests);
    resetExecutionRunThresholdDrafts(project.metricThresholds.executionRuns);
    resetPipelineThresholdDrafts(project.metricThresholds.pipelines);
  }, [
    project,
    resetExecutionRunThresholdDrafts,
    resetPipelineThresholdDrafts,
    resetSuiteThresholdDrafts,
    resetTestThresholdDrafts,
  ]);

  return {
    projectQuery,
    project,
    updateProject,
    rotateProjectApiKey,
    deleteProject,
    testProjectPipelineWebhook,
    wipeProjectData,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    mutationError,
    apiKeyError,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    notify,
    navigate,
  };
};
